import React from "react";
import PropTypes from "prop-types";
import { Download } from "lucide-react";

import Filters from "../../../components/Filter";
import { UIButton } from "../../../components/ui";

const ApplicantsListFilters = ({
  applicants,
  searchQuery,
  onSearchChange,
  filters,
  filterOptions,
  lookupOptions,
  eventShift,
  onFilterChange,
  onDownloadProfiles,
  showDownloadAction,
  selectedCount,
}) => {
  const {
    profileStatusOptions,
    applicantStatusOptions,
    genderOptions,
    heightOptions,
    weightOptions,
    ageOptions,
  } = filterOptions;

  const {
    roles,
    disabilities,
    chronicDiseases,
    cities,
    countries,
    languages,
    updatedSpeaking,
    tags,
    approvedCompanies,
  } = lookupOptions;

  return (
    <Filters
      searchInput={{
        placeholder: applicants.listApplicants.searchPlaceHolder,
        name: "search",
        value: searchQuery,
        onChange: onSearchChange,
      }}
      renderActionBeforeFilterToggle={
        showDownloadAction ? (
          <div className="filter-toggle-btn">
            <UIButton
              disabled={selectedCount === 0}
              variant="primary"
              appearance="outline"
              size="large"
              startIcon={<Download size={16} aria-hidden="true" />}
              onClick={onDownloadProfiles}
            >
              {applicants.applicantDetails.downloadProfiles}
              {selectedCount > 0 ? (
                <span className="mx-1">({selectedCount})</span>
              ) : null}
            </UIButton>
          </div>
        ) : null
      }
      filters={[
        {
          type: "filter",
          name: "profileStatus",
          placeholder: applicants.listApplicants.profileStatus,
          label: applicants.listApplicants.profileStatus,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.profile_status,
          options: profileStatusOptions,
          onChange: (value) => {
            onFilterChange({ profile_status: value });
          },
        },
        {
          type: "filter",
          name: "applicantStatus",
          placeholder: applicants.listApplicants.applicantStatus,
          label: applicants.listApplicants.applicantStatus,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.is_assigned_to_event,
          options: applicantStatusOptions,
          onChange: (value) =>
            onFilterChange({ is_assigned_to_event: value }),
        },
        {
          type: "filter",
          name: "role",
          placeholder: applicants.listApplicants.role,
          label: applicants.listApplicants.role,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.role_id,
          options: roles,
          onChange: (value) => onFilterChange({ role_id: value }),
        },
        {
          type: "filter",
          name: "gender",
          placeholder: applicants.listApplicants.gender,
          label: applicants.listApplicants.gender,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.gender,
          options: genderOptions,
          onChange: (value) => onFilterChange({ gender: value }),
        },
        {
          type: "filter",
          name: "height",
          placeholder: applicants.listApplicants.height,
          label: applicants.listApplicants.height,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.height,
          options: heightOptions,
          onChange: (value) => onFilterChange({ height: value }),
        },
        {
          type: "filter",
          name: "weight",
          placeholder: applicants.listApplicants.weight,
          label: applicants.listApplicants.weight,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.weight,
          options: weightOptions,
          onChange: (value) => onFilterChange({ weight: value }),
        },
        {
          type: "filter",
          name: "disability",
          placeholder: applicants.listApplicants.disability,
          label: applicants.listApplicants.disability,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.disability_id,
          options: disabilities
            ? [
                ...disabilities,
                { id: 0, name: applicants.listApplicants.noDisability },
              ]
            : [],
          onChange: (value) => onFilterChange({ disability_id: value }),
        },
        {
          type: "filter",
          name: "diseases",
          placeholder: applicants.listApplicants.diseases,
          label: applicants.listApplicants.diseases,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.chronic_disease_id,
          options: chronicDiseases
            ? [
                ...chronicDiseases,
                { id: 0, name: applicants.listApplicants.noDisease },
              ]
            : [],
          onChange: (value) =>
            onFilterChange({ chronic_disease_id: value }),
        },
        {
          type: "filter",
          name: "age",
          placeholder: applicants.listApplicants.age,
          label: applicants.listApplicants.age,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.age,
          options: ageOptions,
          onChange: (value) => onFilterChange({ age: value }),
        },
        {
          type: "filter",
          name: "city",
          placeholder: applicants.listApplicants.cityOfResidence,
          label: applicants.listApplicants.cityOfResidence,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.city_id,
          options: cities,
          onChange: (value) => onFilterChange({ city_id: value }),
        },
        {
          type: "filter",
          name: "nationality",
          placeholder: applicants.listApplicants.nationality,
          label: applicants.listApplicants.nationality,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.nationality_id,
          options: countries,
          onChange: (value) => onFilterChange({ nationality_id: value }),
        },
        {
          type: "filter",
          name: "language",
          placeholder: applicants.listApplicants.language,
          label: applicants.listApplicants.language,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.language_id,
          options: languages,
          onChange: (value) => onFilterChange({ language_id: value }),
        },
        {
          type: "filter",
          name: "Speaking Proficiency",
          placeholder: applicants.listApplicants.speaking,
          label: applicants.listApplicants.speaking,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters?.speaking_proficiency,
          options: updatedSpeaking,
          onChange: (value) =>
            onFilterChange({ speaking_proficiency: value }),
        },
        {
          type: "filter",
          name: "tag",
          placeholder: applicants.listApplicants.tag,
          label: applicants.listApplicants.tag,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.tag_id,
          options: tags,
          onChange: (value) => onFilterChange({ tag_id: value }),
        },
        {
          type: "filter",
          name: "favouriteCompany",
          placeholder: applicants.listApplicants.company,
          label: applicants.listApplicants.company,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters.favourite_company_id,
          options: approvedCompanies,
          onChange: (value) => onFilterChange({ favourite_company_id: value }),
        },
        filters?.role_id?.id && {
          type: "filter",
          name: "shift",
          placeholder: applicants.listApplicants.shift,
          label: applicants.listApplicants.shift,
          wrapperClass: "applicants-page-container__filter-field",
          value: filters?.working_shift,
          options: eventShift,
          onChange: (value) => onFilterChange({ working_shift: value }),
        },
      ]}
      classNames={{
        container: "applicants-page-container__filters",
        base: "",
        search: "mb-3 mb-md-0",
        filter: "",
      }}
    />
  );
};

ApplicantsListFilters.propTypes = {
  applicants: PropTypes.object.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  lookupOptions: PropTypes.object.isRequired,
  eventShift: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDownloadProfiles: PropTypes.func.isRequired,
  showDownloadAction: PropTypes.bool.isRequired,
  selectedCount: PropTypes.number.isRequired,
};

export default ApplicantsListFilters;
